<template>
    <div class="league-stats">
        <!-- the awards of the season: the leader of each category gets the trophy -->
        <section v-if="type !== 'cup'" class="cl-section awards">
            <header class="awards__head">
                <span class="awards__trophy"><v-icon color="#1b1b1b">mdi-trophy</v-icon></span>
                <span class="awards__titles">
                    <span class="awards__eyebrow">Βραβεία σεζόν</span>
                    <span class="awards__title">Top Performers</span>
                </span>
            </header>

            <div class="awards__grid">
                <article v-for="award in leaders" :key="award.title" class="award">
                    <div class="award__ribbon">
                        <v-icon small color="#1b1b1b">{{ award.icon }}</v-icon>
                        <span>{{ award.title }}</span>
                    </div>

                    <template v-if="award.items && award.items.length">
                        <!-- the holder of the award -->
                        <div class="award__winner">
                            <div class="award__photo">
                                <img :src="photo(award.items[0])" alt="" @error="$event.target.src = defaultPlayerImg">
                                <span class="award__rank">1</span>
                            </div>
                            <div class="award__winner-name">{{ award.items[0].name }}</div>
                            <div class="award__winner-team">
                                <span class="cl-logo award__team-logo">
                                    <img :src="`${assetsUrl}/${award.items[0].teamLogoUrl}`" alt="">
                                </span>
                                <span v-if="award.items[0].jersey !== null && award.items[0].jersey !== undefined">#{{ award.items[0].jersey }}</span>
                            </div>
                            <div class="award__figure">
                                {{ award.items[0][award.value] }}
                                <span>{{ award.unit }}</span>
                            </div>
                        </div>

                        <!-- the chasers -->
                        <ol class="award__rest">
                            <li v-for="(item, index) in award.items.slice(1, 5)" :key="index" class="award__row">
                                <span class="award__row-rank">{{ index + 2 }}</span>
                                <img :src="photo(item)" alt="" class="award__row-photo" @error="$event.target.src = defaultPlayerImg">
                                <span class="award__row-name">{{ item.name }}</span>
                                <span class="cl-logo award__row-logo">
                                    <img :src="`${assetsUrl}/${item.teamLogoUrl}`" alt="">
                                </span>
                                <span class="award__row-figure">{{ item[award.value] }}</span>
                            </li>
                        </ol>
                    </template>
                    <div v-else class="award__empty">Δεν υπάρχουν ακόμη στοιχεία</div>
                </article>
            </div>
        </section>

        <section class="cl-section">
            <section-heading title="Leaders by stat" icon="mdi-podium">
                <match-type-choice light :options="['Παίκτες', 'Ομάδα']" @setType="value => option = value" />
            </section-heading>
            <div class="league-stats__cards">
                <stat-card
                    v-for="(statHeader, index) in statsHeaders"
                    :key="index"
                    :loading="loading"
                    :stat-header="statHeader"
                    :stats="getDisplayData(statHeader)"
                    :option="option === 1"
                    @scrollToStats="scrollToStats()"
                />
            </div>
        </section>

        <section ref="stats" class="cl-section">
            <section-heading :title="option ? 'Team stats' : 'Player stats'" icon="mdi-table">
                <match-type-choice light :options="['Μέσοι Όροι', 'Σύνολο']" @setType="value => option2 = value" />
            </section-heading>
            <players-stats v-if="!option" :players="playerStats" :option="option2" :generic="true" />
            <teams-stats v-else :teams="stasData" :option="option2" :generic="true" />
        </section>
    </div>
</template>

<script>
    import StatCard from '@/components/StatCard'
    import statsService from '@/services/stats.js'
    import playerService from '@/services/player.js'
    import PlayersStats from '@/components/PlayersStats'
    import TeamsStats from '@/components/TeamsStats'
    import SectionHeading from '@/components/SectionHeading'
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import { foulsOf } from '@/utils/teamStats'

    export default {
        components: { StatCard, PlayersStats, TeamsStats, SectionHeading, MatchTypeChoice },
        props: {
            league: {
                type: Object,
                required: true
            },
            season: {
                type: Number,
                required: true
            },
            type: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png'),
                assetsUrl: 'https://storage.googleapis.com',
                statsHeaders: [
                    { text: 'PTS', value: 'points' },
                    { text: '2P Made', value: 'twoPointsMade' },
                    { text: '3P Made', value: 'threePointsMade' },
                    { text: 'FT Made', value: 'freeThrowsMade' },
                    { text: 'OR', value: 'offensiveRebounds' },
                    { text: 'DR', value: 'defensiveRebounds' },
                    { text: 'TR', value: 'rebounds' },
                    { text: 'AST', value: 'assists' },
                    { text: 'STL', value: 'steals' },
                    { text: 'TO', value: 'turnovers' },
                    { text: 'BLK', value: 'blockedShots' },
                    { text: 'FC', value: 'fouls' }
                ],
                stasData: null,
                playerStats: [],
                teamStats: [],
                option: 0,
                option2: 0,
                phase: 'regular_season',
                loading: false,
                maxRound: 0,
                topPerformers: []
            }
        },
        computed: {
            // the three leaderboards of the top performers
            leaders() {
                return [
                    { title: 'Top Scorer', icon: 'mdi-basketball', unit: 'Πόντοι', items: this.topPerformers.topScorePlayers, value: 'score' },
                    { title: 'The Bomber', icon: 'mdi-bullseye-arrow', unit: 'Τρίποντα', items: this.topPerformers.topThreePointsMadePlayers, value: 'threePointsMade' },
                    { title: 'The Efficient', icon: 'mdi-lightning-bolt', unit: 'Efficiency', items: this.topPerformers.topPirPlayers, value: 'pir' }
                ]
            }
        },
        watch: {
            league: {
                handler() {
                    this.fetchStats()
                    if (this.type !== 'cup') {
                        this.getTopPlayers()
                    }
                }
            },
            phase: {
                handler() {
                    this.fetchStats()
                }
            }
        },
        created() {
            this.fetchStats()
            if (this.type !== 'cup') {
                this.getTopPlayers()
            }
        },
        methods: {
            photo(item) {
                return item.imgUrl ? `${this.assetsUrl}/${item.imgUrl}` : this.defaultPlayerImg
            },
            getTopPlayers() {
                playerService.getTopPerformersByLeague(this.league.id, this.season).then(resp => {
                    this.topPerformers = resp.data
                })
            },
            getDisplayData(statHeader) {
                if (this.maxRound > 0) {
                    const temp = JSON.parse(JSON.stringify(this.option === 1 ? this.statsData : this.playerStats.filter((element) => {
                        return element.stats.length >= (this.maxRound * 0.55)
                    })))
                    temp.sort((a, b) => b.avg[statHeader.value] - a.avg[statHeader.value])
                    return temp.length > 5 ? temp.slice(0, 5) : temp
                } else {
                    return []
                }
            },
            fetchStats() {
                if (this.season) {
                    this.loading = true
                    this.$store.dispatch('setLeagueFilter', this.league)
                    if (this.type === 'cup') {
                        statsService.getStatsByCupLeague(this.league.id, this.season).then(resp => {
                            this.statsData = resp.data
                            this.calculateTeamsStats(resp.data)
                            this.calculatePlayersStats(resp.data)
                            this.getPlayersStats()
                        })
                    } else {
                        statsService.getStatsByLeague(this.league.id, this.phase, this.season).then(resp => {
                            this.statsData = resp.data
                            this.calculateTeamsStats(resp.data)
                            this.calculatePlayersStats(resp.data)
                            this.getPlayersStats()
                        })
                    }
                }
            },
            calculateTeamsStats(data) {
                data.forEach(team => {
                    this.teamStats = []
                    team.players.forEach(player => {
                        this.teamStats = this.teamStats.concat(player.stats)
                    })
                    team.stats = this.groupByMatch()
                    team.avg = this.getAverageStats(team)
                    team.cum = this.getCummulativeStats(team)
                    team.gp = team.stats.length
                    if (team.stats.length > this.maxRound) {
                        this.maxRound = team.stats.length
                    }
                })
                this.stasData = data
            },
            findMatches() {
                const matches = new Set()
                this.teamStats.forEach(stat => {
                    matches.add(stat.matchId)
                })
                return matches
            },
            groupByMatch() {
                const matches = this.findMatches()
                const data = []
                matches.forEach(match => {
                    const stats = {
                        points: 0,
                        rebounds: 0,
                        assists: 0,
                        steals: 0,
                        turnovers: 0,
                        blockedShots: 0,
                        twoPointsMade: 0,
                        twoPointAttempts: 0,
                        threePointsMade: 0,
                        threePointAttempts: 0,
                        freeThrowsMade: 0,
                        freeThrowAttempts: 0,
                        offensiveRebounds: 0,
                        defensiveRebounds: 0,
                        personalFouls: 0,
                        fouls: 0
                    }
                    this.teamStats.forEach(stat => {
                        if (match === stat.matchId) {
                            for (const key of Object.keys(stats)) {
                                stats[key] += key === 'fouls' ? foulsOf(stat) : stat[key]
                            }
                        }
                    })
                    data.push(stats)
                })
                return data
            },
            getPlayersStats() {
                this.playerStats = []
                this.stasData.forEach(team => {
                    this.playerStats = this.playerStats.concat(team.players)
                })
                this.loading = false
                return this.playerStats
            },
            calculatePlayersStats(data) {
                data.forEach(team => {
                    team.players.forEach(player => {
                        player.team = team.name
                        player.teamLogo = team.logo
                        player.avg = this.getAverageStats(player)
                        player.cum = this.getCummulativeStats(player)
                    })
                })
                this.stasData = data
            },
            getCummulativeStats(entity) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                entity.stats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats(entity) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                const temp = entity.stats
                temp.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })

                Object.keys(stats).forEach(element => {
                    if (temp.length) {
                        stats[element] = stats[element] / temp.length
                    } else {
                        stats[element] = 0
                    }
                })
                return stats
            },
            scrollToStats() {
                this.$refs.stats.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }
</script>

<style scoped>
/* ---------- the awards ---------- */
.awards {
    --gold: #f2c14e;
    --gold-gradient: linear-gradient(135deg, #ffe08a 0%, #f2c14e 45%, #c8912a 100%);

    position: relative;
    padding: 26px 24px 24px;
    border-radius: 16px;
    background:
        radial-gradient(circle at 12% 0%, rgba(242, 193, 78, 0.18) 0%, rgba(242, 193, 78, 0) 40%),
        radial-gradient(circle at 100% 100%, rgba(3, 229, 183, 0.14) 0%, rgba(3, 229, 183, 0) 45%),
        #1b1b1b;
    box-shadow: 0 18px 44px rgba(35, 35, 35, 0.22);
    overflow: hidden;
}

.awards::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 4px;
    background: var(--gold-gradient);
}

.awards__head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 22px;
}

.awards__trophy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: var(--gold-gradient);
    box-shadow: 0 8px 22px rgba(242, 193, 78, 0.35);
}

.awards__titles {
    display: flex;
    flex-direction: column;
    text-align: left;
}

.awards__eyebrow {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--gold);
}

.awards__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.2rem;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #fff;
}

.awards__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
    gap: 18px;
}

/* ---------- one award ---------- */
.award {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0 0 8px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.02) 100%);
    border: 1px solid rgba(242, 193, 78, 0.28);
    color: #fff;
    overflow: hidden;
}

.award__ribbon {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: center;
    margin-top: -1px;
    padding: 6px 18px 7px;
    border-radius: 0 0 12px 12px;
    background: var(--gold-gradient);
    color: #1b1b1b;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.25rem;
    line-height: 1;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.award__winner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 18px 16px 16px;
    text-align: center;
}

.award__photo {
    position: relative;
    width: 104px;
    height: 104px;
    margin-bottom: 6px;
    padding: 3px;
    border-radius: 50%;
    background: var(--gold-gradient);
    box-shadow: 0 10px 28px rgba(242, 193, 78, 0.28);
}

.award__photo img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #2a2a2a;
    object-fit: cover;
    object-position: top;
}

.award__rank {
    position: absolute;
    right: -2px;
    bottom: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #1b1b1b;
    border: 2px solid var(--gold);
    color: var(--gold);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 900;
}

.award__winner-name {
    font-family: Roboto, Arial, sans-serif;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.2;
}

.award__winner-team {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.6);
}

.award__team-logo {
    width: 28px;
    height: 28px;
}

.award__figure {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 4px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 3.2rem;
    line-height: 1;
    background-image: var(--gold-gradient);
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.award__figure span {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    -webkit-text-fill-color: rgba(255, 255, 255, 0.55);
}

.award__rest {
    margin: 0 12px;
    padding: 0;
    list-style: none;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.award__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
}

.award__row:last-child {
    border-bottom: 0;
}

.award__row-rank {
    width: 18px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
}

.award__row-photo {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #2a2a2a;
    object-fit: cover;
    object-position: top;
}

.award__row-name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.85rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.award__row-logo {
    width: 26px;
    height: 26px;
}

.award__row-figure {
    min-width: 38px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    color: #03e5b7;
    text-align: right;
}

.award__empty {
    padding: 40px 16px;
    font-family: Roboto, Arial, sans-serif;
    color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 700px) {
    .awards {
        padding: 20px 12px 14px;
        border-radius: 12px;
    }

    .awards__title {
        font-size: 1.8rem;
    }
}

.league-stats__cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
    gap: 16px;
}

@media (max-width: 700px) {
    .league-stats__cards {
        grid-template-columns: 1fr;
    }
}
</style>

<style scoped>
.v-chip::before {
    background-color: white !important;
}

.selected-chip {
    color: white;
}
</style>
